import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import {
  ClipboardList,
  Upload,
  FileText,
  Trash2,
  Download,
  Eye,
  User,
  Calendar,
  Loader2,
  Plus,
  Sparkles,
  Search,
  ChevronRight
} from "lucide-react";
import ProfiloCapelliForm, { ProfiloCapelliView, PROFILO_OPTIONS, PROFILO_FIELDS } from "@/components/ProfiloCapelliForm";

export default function Consulenze() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [consulenze, setConsulenze] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Dialog nuova consulenza
  const [nuovaConsulenzaOpen, setNuovaConsulenzaOpen] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [profiloCapelli, setProfiloCapelli] = useState({});
  const [noteConsulenza, setNoteConsulenza] = useState("");
  
  // Dialog upload PDF (legacy)
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedClienteUpload, setSelectedClienteUpload] = useState("");
  const [descrizione, setDescrizione] = useState("");
  const [uploading, setUploading] = useState(false);
  
  // Dialog dettaglio consulenza
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedConsulenza, setSelectedConsulenza] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [consulenzeRes, clientiRes] = await Promise.all([
        axios.get(`${API}/consulenze`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/clienti`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setConsulenze(consulenzeRes.data);
      setClienti(clientiRes.data);
    } catch (error) {
      console.error("Errore nel caricamento dati:", error);
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  };

  // Filtra clienti per ricerca
  const clientiFiltrati = clienti.filter(c => {
    const fullName = `${c.nome} ${c.cognome}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase()) || 
           c.telefono?.includes(searchTerm);
  });

  // Salva nuova consulenza con profilo capelli
  const handleSaveConsulenza = async () => {
    if (!selectedCliente) {
      toast.error("Seleziona un cliente");
      return;
    }
    
    // Verifica che almeno un campo del profilo sia compilato
    const hasProfiloData = Object.values(profiloCapelli).some(v => v && v !== "");
    if (!hasProfiloData) {
      toast.error("Compila almeno un campo del profilo capelli");
      return;
    }
    
    setSaving(true);
    try {
      // Aggiorna il profilo capelli del cliente
      await axios.post(
        `${API}/clienti/${selectedCliente.id}/profilo-capelli`,
        profiloCapelli,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Crea la consulenza
      const consulenzaData = {
        cliente_id: selectedCliente.id,
        cliente_nome: `${selectedCliente.nome} ${selectedCliente.cognome || ""}`.trim(),
        tipo_cliente: "esistente",
        tipo_consulenza: "profilazione",
        colore_naturale: profiloCapelli.colore_naturale || "",
        colore_attuale: profiloCapelli.colore_attuale || "",
        percentuale_bianchi: profiloCapelli.percentuale_bianchi || "",
        tipo_capello: profiloCapelli.tipo_capello || "",
        spessore_capello: profiloCapelli.spessore || "",
        densita: profiloCapelli.densita || "",
        stato_capelli: profiloCapelli.stato_capello || "",
        note_tecniche: noteConsulenza
      };
      
      await axios.post(
        `${API}/consulenze`,
        consulenzaData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Consulenza salvata e profilo cliente aggiornato!");
      setNuovaConsulenzaOpen(false);
      setSelectedCliente(null);
      setProfiloCapelli({});
      setNoteConsulenza("");
      setSearchTerm("");
      setTimeout(() => fetchData(), 150);
    } catch (error) {
      console.error("Errore salvataggio:", error);
      toast.error("Errore nel salvataggio della consulenza");
    } finally {
      setSaving(false);
    }
  };

  // Upload PDF (legacy)
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error("Seleziona un file PDF");
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Il file deve essere inferiore a 10MB");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Seleziona un file PDF");
      return;
    }
    if (!selectedClienteUpload) {
      toast.error("Seleziona un cliente");
      return;
    }

    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('cliente_id', selectedClienteUpload);
      
      const cliente = clienti.find(c => c.id === selectedClienteUpload);
      formData.append('cliente_nome', cliente ? `${cliente.nome} ${cliente.cognome || ''}`.trim() : '');
      formData.append('descrizione', descrizione);

      await axios.post(`${API}/consulenze/upload-pdf`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success("Consulenza PDF caricata!");
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setSelectedClienteUpload("");
      setDescrizione("");
      setTimeout(() => fetchData(), 150);
    } catch (error) {
      console.error("Errore upload:", error);
      toast.error("Errore nel caricamento del file");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Eliminare questa consulenza?")) return;
    
    try {
      await axios.delete(`${API}/consulenze/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Consulenza eliminata");
      setTimeout(() => fetchData(), 100);
    } catch (error) {
      console.error("Errore eliminazione:", error);
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleDownload = async (consulenza) => {
    if (!consulenza.file_url) {
      toast.error("Nessun file disponibile");
      return;
    }
    
    try {
      const response = await axios.get(`${API}/consulenze/${consulenza.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `consulenza_${consulenza.cliente_nome}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Errore download:", error);
      toast.error("Errore nel download");
    }
  };

  const openDetail = (consulenza) => {
    setSelectedConsulenza(consulenza);
    setDetailDialogOpen(true);
  };

  // Raggruppa consulenze per cliente
  const consulenzePerCliente = consulenze.reduce((acc, c) => {
    const key = c.cliente_id || 'sconosciuto';
    if (!acc[key]) acc[key] = [];
    acc[key].push(c);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20" data-testid="consulenze-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800" style={{ fontFamily: 'Playfair Display, serif' }}>
            Schede Consulenza
          </h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">
            Profilazione capelli e consulenze clienti
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setNuovaConsulenzaOpen(true)} 
            className="bg-amber-600 hover:bg-amber-700 gap-2"
          >
            <Sparkles className="w-4 h-4" />
            Nuova Consulenza
          </Button>
          <Button 
            variant="outline"
            onClick={() => setUploadDialogOpen(true)} 
            className="gap-2"
          >
            <Upload className="w-4 h-4" />
            Carica PDF
          </Button>
        </div>
      </div>

      {/* Lista Consulenze */}
      {consulenze.length === 0 ? (
        <Card className="border-0 shadow-lg">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mb-4">
              <Sparkles className="w-10 h-10 text-amber-500" />
            </div>
            <p className="text-slate-500 text-lg mb-2">Nessuna consulenza registrata</p>
            <p className="text-slate-400 text-sm mb-6 text-center max-w-md">
              Crea la prima consulenza per profilare i capelli dei tuoi clienti e avere statistiche dettagliate
            </p>
            <Button onClick={() => setNuovaConsulenzaOpen(true)} className="gap-2 bg-amber-600 hover:bg-amber-700">
              <Sparkles className="w-4 h-4" />
              Nuova Consulenza
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {consulenze.map((consulenza) => {
            const isPdf = consulenza.file_url;
            const hasProfilo = consulenza.tipo_consulenza === 'profilazione' || consulenza.colore_naturale;
            
            return (
              <Card 
                key={consulenza.id}
                className="border-0 shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
                onClick={() => openDetail(consulenza)}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0 ${
                      isPdf ? 'bg-red-100' : 'bg-amber-100'
                    }`}>
                      {isPdf ? (
                        <FileText className="w-7 h-7 text-red-600" />
                      ) : (
                        <Sparkles className="w-7 h-7 text-amber-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-800 truncate">
                        {consulenza.cliente_nome || "Cliente"}
                      </h3>
                      {hasProfilo && consulenza.colore_naturale && (
                        <p className="text-sm text-slate-500 mt-1">
                          {PROFILO_OPTIONS.colore_naturale.find(o => o.value === consulenza.colore_naturale)?.label || consulenza.colore_naturale}
                          {consulenza.tipo_capello && ` • ${PROFILO_OPTIONS.tipo_capello.find(o => o.value === consulenza.tipo_capello)?.label || consulenza.tipo_capello}`}
                        </p>
                      )}
                      {consulenza.descrizione && !hasProfilo && (
                        <p className="text-sm text-slate-500 truncate mt-1">
                          {consulenza.descrizione}
                        </p>
                      )}
                      <div className="flex items-center gap-2 text-xs text-slate-400 mt-2">
                        <Calendar className="w-3 h-3" />
                        {format(new Date(consulenza.created_at), "dd MMM yyyy", { locale: it })}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog Nuova Consulenza */}
      <Dialog open={nuovaConsulenzaOpen} onOpenChange={setNuovaConsulenzaOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-600" />
              Nuova Consulenza - Profilo Capelli
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Step 1: Seleziona Cliente */}
            {!selectedCliente ? (
              <div className="space-y-4">
                <Label>Cerca Cliente</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Cerca per nome o telefono..."
                    className="pl-10"
                  />
                </div>
                
                <div className="max-h-60 overflow-y-auto space-y-1">
                  {clientiFiltrati.slice(0, 20).map(cliente => (
                    <button
                      key={cliente.id}
                      onClick={() => {
                        setSelectedCliente(cliente);
                        setProfiloCapelli(cliente.profilo_capelli || {});
                      }}
                      className="w-full p-3 text-left hover:bg-blue-50 rounded-lg flex items-center gap-3 border border-slate-100"
                    >
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold">
                        {cliente.nome?.charAt(0)?.toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium">{cliente.nome} {cliente.cognome}</p>
                        <p className="text-sm text-slate-500">{cliente.telefono}</p>
                      </div>
                    </button>
                  ))}
                  {clientiFiltrati.length === 0 && (
                    <p className="text-slate-400 text-center py-8">Nessun cliente trovato</p>
                  )}
                </div>
              </div>
            ) : (
              <>
                {/* Cliente selezionato */}
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold">
                      {selectedCliente.nome?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium">{selectedCliente.nome} {selectedCliente.cognome}</p>
                      <p className="text-sm text-slate-500">{selectedCliente.telefono}</p>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      setSelectedCliente(null);
                      setProfiloCapelli({});
                    }}
                  >
                    Cambia
                  </Button>
                </div>
                
                {/* Form Profilo Capelli */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-amber-700">
                    <Sparkles className="w-5 h-5" />
                    <h3 className="font-semibold">Le 10 Domande della Consulenza</h3>
                  </div>
                  
                  <ProfiloCapelliForm 
                    profilo={profiloCapelli}
                    onChange={setProfiloCapelli}
                  />
                  
                  <div className="space-y-2">
                    <Label>Note Consulenza</Label>
                    <Textarea
                      value={noteConsulenza}
                      onChange={(e) => setNoteConsulenza(e.target.value)}
                      placeholder="Osservazioni, raccomandazioni, prodotti consigliati..."
                      rows={3}
                    />
                  </div>
                </div>
                
                <Button 
                  onClick={handleSaveConsulenza}
                  disabled={saving}
                  className="w-full bg-amber-600 hover:bg-amber-700 gap-2"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  Salva Consulenza
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Dettaglio Consulenza */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedConsulenza?.file_url ? (
                <FileText className="w-5 h-5 text-red-600" />
              ) : (
                <Sparkles className="w-5 h-5 text-amber-600" />
              )}
              Consulenza - {selectedConsulenza?.cliente_nome}
            </DialogTitle>
          </DialogHeader>
          
          {selectedConsulenza && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Calendar className="w-4 h-4" />
                {format(new Date(selectedConsulenza.created_at), "dd MMMM yyyy 'alle' HH:mm", { locale: it })}
              </div>
              
              {/* Se è un PDF */}
              {selectedConsulenza.file_url && (
                <div className="flex gap-2">
                  <Button
                    onClick={() => window.open(`${API}/consulenze/${selectedConsulenza.id}/view`, '_blank')}
                    className="flex-1 gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    Visualizza PDF
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleDownload(selectedConsulenza)}
                    className="flex-1 gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Scarica
                  </Button>
                </div>
              )}
              
              {/* Se è una consulenza con profilo */}
              {(selectedConsulenza.colore_naturale || selectedConsulenza.tipo_capello) && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-amber-700 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Profilo Capelli
                  </h4>
                  <ProfiloCapelliView profilo={{
                    colore_naturale: selectedConsulenza.colore_naturale,
                    colore_attuale: selectedConsulenza.colore_attuale,
                    percentuale_bianchi: selectedConsulenza.percentuale_bianchi,
                    tipo_capello: selectedConsulenza.tipo_capello,
                    spessore: selectedConsulenza.spessore_capello,
                    densita: selectedConsulenza.densita,
                    lunghezza: selectedConsulenza.lunghezza,
                    stato_capello: selectedConsulenza.stato_capelli,
                    cuoio_capelluto: selectedConsulenza.cuoio_capelluto,
                    trattamenti_chimici: selectedConsulenza.trattamenti_chimici
                  }} />
                </div>
              )}
              
              {selectedConsulenza.note_tecniche && (
                <div className="space-y-2">
                  <h4 className="font-semibold text-slate-700">Note</h4>
                  <p className="text-slate-600 text-sm bg-slate-50 p-3 rounded-lg">
                    {selectedConsulenza.note_tecniche}
                  </p>
                </div>
              )}
              
              <div className="pt-4 border-t flex justify-end">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    handleDelete(selectedConsulenza.id);
                    setDetailDialogOpen(false);
                  }}
                  className="gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Elimina
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Upload PDF (legacy) */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-600" />
              Carica Consulenza PDF
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Cliente *</Label>
              <Select value={selectedClienteUpload} onValueChange={setSelectedClienteUpload}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona cliente..." />
                </SelectTrigger>
                <SelectContent>
                  {clienti.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.nome} {c.cognome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>File PDF *</Label>
              <Input
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
              />
              {selectedFile && (
                <p className="text-sm text-green-600">
                  File selezionato: {selectedFile.name}
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Input
                value={descrizione}
                onChange={(e) => setDescrizione(e.target.value)}
                placeholder="Es: Consulenza colore maggio 2024"
              />
            </div>
            
            <Button 
              onClick={handleUpload} 
              disabled={uploading || !selectedFile || !selectedClienteUpload}
              className="w-full gap-2"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              Carica PDF
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
